import { useAuth0 } from '@auth0/auth0-react';
import { Loading } from '@components';
import { StudioExpiredAlert } from '@components/StudioExpiredAlert/StudioExpiredAlert';
import { OtherErrorAlert } from '@components/OtherErrorAlert/OtherErrorAlert';
// import { Software } from '@pages/Product/Software';
// import { Miscellaneous } from '@pages/Product/Miscellaneous';
// import { StudioService } from '@pages/Product/StudioService';
import { UserBlockedAlert } from '@components/UserBlockedAlert/UserBlockedAlert';
import { useState } from '@hookstate/core';
import { Navigation } from '@layout';
import {
  AddNotifications,
  Asset,
  Forum,
  Onboarding,
  Products,
  Reporting,
  Updates,
  IdentificationRequests,
  ActivityHistory,
} from '@pages';
import { ConfigurationPage } from '@pages/ConfigurationPage/ConfigurationPage';
import { OnboardingFile } from '@pages/Onboarding/components';
import { api, onboarding } from '@services';
import { getProfile } from '@services/queries/profile';
import {
  updateCookieKeyId,
  updateCookiePolicy,
  updateCookieSignature,
  updateUserStudioId,
} from '@store/redux/features/auth';
import { useAppDispatch } from '@store/redux/store';
import { useEffect, useState as classicUseState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { getRefreshInterceptor } from '@utils/getRefreshInterceptor';
import { Tasks } from '@pages/Tasks/Tasks';
import { Documents } from '@pages/Documents/Documents';

const NavigationRoutes = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const cookies = new Cookies();
  const dispatch = useAppDispatch();

  const hasSetAccessToken = useState(false);
  const [showErrorExpired, setShowErrorExpired] =
    classicUseState<boolean>(false);
  const [showOtherError, setShowOtherError] = classicUseState<boolean>(false);
  const [showErrorBlocked, setShowErrorBlocked] =
    classicUseState<boolean>(false);

  async function authEffect() {
    // This check is unnecessary because we MUST be authed at this point.
    if (isAuthenticated) {
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      });
      api.defaults.headers.authorization = `Bearer ${token}`;
      onboarding.defaults.headers.authorization = `Bearer ${token}`;
      api.interceptors.request.use(
        getRefreshInterceptor(getAccessTokenSilently),
      );
      try {
        const authCookie = await api.get(`auth0/cookie`);
        const user = await getProfile();
        if (authCookie && cookies) {
          cookies.set('CloudFront-Policy', authCookie.data.policy);
          cookies.set('CloudFront-Signature', authCookie.data.bSignature);
          cookies.set('CloudFront-Key-Pair-Id', authCookie.data.bKeyPairId);
          dispatch(updateCookiePolicy(authCookie.data.policy));
          dispatch(updateCookieSignature(authCookie.data.bSignature));
          dispatch(updateCookieKeyId(authCookie.data.bKeyPairId));
          dispatch(updateUserStudioId(user.userGroup.studioId));
        }

        await api.get(`/users/userHasAccess`);
      } catch (error) {
        if ((error as any).response?.statusText === 'Forbidden') {
          setShowErrorExpired(true);
        }
        if ((error as any).response?.data?.message === 'User blocked') {
          setShowErrorBlocked(true);
        }
        console.error('Silent error on auth', error); // todo: add sentry.
        setShowOtherError(true);
      }

      hasSetAccessToken.set(true);
    } else {
      delete api.defaults.headers.authorization;
      delete onboarding.defaults.headers.authorization;

      hasSetAccessToken.set(false);
    }
  }
  useEffect(() => {
    authEffect();
  }, []);

  if (!hasSetAccessToken.get()) {
    return <Loading />;
  }

  if (showOtherError) {
    return <OtherErrorAlert />;
  }
  if (showErrorExpired) {
    return <StudioExpiredAlert />;
  }

  if (showErrorBlocked) {
    return <UserBlockedAlert />;
  }

  return (
    <Navigation>
      <Switch>
        <Route path="/configuration">
          <ConfigurationPage />
        </Route>
        <Route path="/updates">
          <Updates />
        </Route>

        <Route exact path="/forum">
          <Forum />
        </Route>
        <Route exact path="/onboarding">
          <Onboarding />
        </Route>
        <Route exact path="/tasks">
          <Tasks />
        </Route>
        <Route exact path="/onboarding/:id">
          <OnboardingFile />
        </Route>
        <Route exact path="/reporting">
          <Reporting />
        </Route>
        <Route exact path="/products">
          <Products />
        </Route>
        <Route exact path="/activity">
          <ActivityHistory />
        </Route>
        <Route exact path="/documents">
          <Documents />
        </Route>
        <Route exact path="/addNotifications">
          <AddNotifications />
        </Route>
        <Route exact path="/equipments/:equipmentId">
          <Asset />
        </Route>
        <Route exact path="/identificationRequests">
          <IdentificationRequests />
        </Route>
        {/* <Route exact path="/software/:softwareId">
          <Software />
        </Route>
        <Route exact path="/miscellaneous/:miscellaneousId">
          <Miscellaneous />
        </Route>
        <Route exact path="/studioService/:studioServiceId">
          <StudioService />
        </Route> */}
        <Route>
          <Redirect to="/activity" />
        </Route>
      </Switch>
    </Navigation>
  );
};

export { NavigationRoutes };
